import { type FC } from 'react';
import { graphql, usePaginationFragment } from 'react-relay';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';
import { NftItemTile } from 'dibs-nft-item-tile/exports/NftItemTile.ts';
import { NftHeartPillProvider } from 'dibs-nft-heart-pill/exports/NftHeartPillProvider.ts';
import { useCurrencyStore } from 'dibs-buyer-layout/exports/currencyStore.ts';
import { NftProfileSectionLoadMore } from './NftProfileSectionLoadMore.tsx';
import { trackPdpImpression, trackPdpRedirect } from './nftCollectorTracking.ts';

import styles from './NftProfileSections.scss';
import { type NftProfileSectionCollection_viewer$key as ViewerRef } from './__generated__/NftProfileSectionCollection_viewer.graphql.ts';
import { type NftProfileSectionCollectionPaginationQuery as PaginationQuery } from './__generated__/NftProfileSectionCollectionPaginationQuery.graphql.ts';

type Props = {
    viewer: ViewerRef;
    isMobile: boolean;
};

export const NftProfileSectionCollection: FC<Props> = ({ isMobile, viewer: viewerRef }) => {
    const { currency } = useCurrencyStore();

    const { data, hasNext, loadNext, isLoadingNext } = usePaginationFragment<
        PaginationQuery,
        ViewerRef
    >(
        graphql`
            fragment NftProfileSectionCollection_viewer on Viewer
            @refetchable(queryName: "NftProfileSectionCollectionPaginationQuery") {
                nftItemSearch(
                    ownerProfileUsernames: [$username]
                    ownerAddresses: [$walletAddress]
                    first: $collectionFirst
                    after: $collectionCursor
                    excludeOwnerCreatedItems: true
                ) @connection(key: "NftProfileSectionCollection_nftItemSearch") {
                    edges {
                        node {
                            serviceId
                            metadata {
                                ... on NftDigitalArtMetadata {
                                    name
                                }
                            }
                            mostRecentAuction(excludeCanceled: true) {
                                ...NftItemTile_nftAuction
                            }
                            ...NftItemTile_nftItem
                        }
                    }
                }
                ...NftItemTile_viewer
                ...NftHeartPillProvider_viewer
            }
        `,
        viewerRef
    );

    return (
        <div className={styles.auctionSectionContainer}>
            <NftHeartPillProvider
                viewer={data}
                nftItemIds={(data.nftItemSearch?.edges || [])
                    .map(edge => edge?.node?.serviceId || null)
                    .filter(filterNulls)}
            >
                <ul className={styles.auctionItems}>
                    {data.nftItemSearch?.edges?.map((edge, index) => {
                        const nftItem = edge?.node;

                        if (!nftItem) {
                            return null;
                        }

                        return (
                            <li className={styles.itemTileWrapper} key={index}>
                                <NftItemTile
                                    tileType="COLLECTOR_TILE"
                                    nftItem={nftItem}
                                    nftAuction={nftItem.mostRecentAuction ?? null}
                                    fireTracking={trackPdpRedirect(
                                        nftItem.serviceId,
                                        nftItem.metadata?.name,
                                        index,
                                        'COLLECTION_TAB'
                                    )}
                                    fireImpressionTracking={trackPdpImpression(
                                        nftItem.serviceId,
                                        nftItem.metadata?.name,
                                        index,
                                        'COLLECTION_TAB'
                                    )}
                                    key={nftItem.serviceId}
                                    viewer={data}
                                    currency={currency}
                                    gaLabel="NFT-Collector"
                                    isMobile={isMobile}
                                />
                            </li>
                        );
                    })}
                </ul>
            </NftHeartPillProvider>

            <NftProfileSectionLoadMore
                hasNext={hasNext}
                isLoadingNext={isLoadingNext}
                onClick={() => loadNext(20)}
            />
        </div>
    );
};
